import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Menu, Text } from '@mantine/core';
import { IconBuildingWarehouse, IconCheck, IconChevronDown } from '@tabler/icons-react';
import { useCurrentWarehouseStore } from './currentWarehouse';
import { useReachableWarehouses, type ReachableWarehouse } from './useReachableWarehouses';

/**
 * Which warehouse you are working in, and how to change it — in the header, on every screen.
 *
 * **Why the header and not a setting.** Permissions resolve per warehouse, so this value decides
 * what the menu offers and what every screen shows. A value with that much reach has to be visible
 * without going to look for it, or somebody spends ten minutes wondering why an item has no stock.
 * Legacy puts it in the same place for the same reason (`_Layout.cshtml` — the site name beside
 * the user, with a button to change it).
 *
 * **Why the code and not the display name.** "DAL" fits a phone header; "Dallas Distribution
 * Center" does not, and the code is what people say on the radio and read off a label.
 */
export function WarehouseSwitcher() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const reachable = useReachableWarehouses();
  const warehouseId = useCurrentWarehouseStore((s) => s.warehouseId);
  const warehouseCode = useCurrentWarehouseStore((s) => s.warehouseCode);
  const setWarehouse = useCurrentWarehouseStore((s) => s.setWarehouse);

  const rows = reachable.data ?? [];

  function choose(warehouse: ReachableWarehouse) {
    if (warehouse.warehouseId === warehouseId) return;

    setWarehouse(warehouse);
    // EVERYTHING cached is about the old warehouse — the permission list, every stock balance,
    // every location, every order. Clearing the lot is blunt and correct; picking which keys to
    // keep would mean maintaining a list of what is warehouse-scoped, and the first thing missed
    // from it is a screen showing another warehouse's stock with no sign that it is wrong.
    void queryClient.invalidateQueries();
  }

  // Nothing to show before a warehouse is settled, and nothing to switch between when there is
  // one: a control whose only option is the current one is furniture.
  if (!warehouseId || rows.length < 2) {
    return warehouseCode ? (
      <Text c="#fff" size="sm" fw={600} visibleFrom="xs" aria-label={t('warehouseSwitcher.current')}>
        {warehouseCode}
      </Text>
    ) : null;
  }

  return (
    <Menu shadow="md" width={260} position="bottom-end">
      <Menu.Target>
        <Button
          size="xs"
          variant="white"
          color="invetrak"
          leftSection={<IconBuildingWarehouse size={14} />}
          rightSection={<IconChevronDown size={14} />}
          aria-label={t('warehouseSwitcher.change')}
        >
          {warehouseCode}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>{t('warehouseSwitcher.label')}</Menu.Label>
        {rows.map((w) => (
          <Menu.Item
            key={w.warehouseId}
            leftSection={
              w.warehouseId === warehouseId ? (
                <IconCheck size={16} />
              ) : (
                <span style={{ width: 16 }} />
              )
            }
            onClick={() => choose(w)}
          >
            <Text size="sm" fw={w.warehouseId === warehouseId ? 700 : 400}>
              {w.warehouseCode}
            </Text>
            <Text size="xs" c="dimmed">
              {w.displayName}
            </Text>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
